import React from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useApp } from "@/context/AppContext";
import { t } from "@/lib/i18n";
import Header from "@/components/Header";
import StepIndicator from "@/components/StepIndicator";
import { Plane, Phone, Package, ArrowRight } from "lucide-react";

const CATS = [
  { id: "gaca", icon: Plane, authority: "GACA", ar_authority: "الطيران المدني" },
  { id: "cst", icon: Phone, authority: "CST", ar_authority: "الاتصالات" },
  { id: "tga", icon: Package, authority: "TGA", ar_authority: "النقل" },
];

const CategoryPage = () => {
  const { language, setCategory } = useApp();
  const navigate = useNavigate();
  if (!language) return <Navigate to="/" replace />;

  const pick = (id) => {
    setCategory(id);
    navigate("/complaint");
  };

  const labelMap = {
    gaca: { title: t(language, "catAir"), desc: t(language, "catAirDesc") },
    cst: { title: t(language, "catPhone"), desc: t(language, "catPhoneDesc") },
    tga: { title: t(language, "catPackage"), desc: t(language, "catPackageDesc") },
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Header />
      <main className="max-w-5xl mx-auto px-6 py-10">
        <StepIndicator current={2} />
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="mt-8 mb-10 text-start"
        >
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight mb-3">
            {t(language, "chooseCategory")}
          </h1>
          <p className="text-slate-500 text-base sm:text-lg">{t(language, "categorySub")}</p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {CATS.map((c, idx) => {
            const Icon = c.icon;
            const l = labelMap[c.id];
            return (
              <motion.button
                key={c.id}
                data-testid={`category-${c.id}`}
                onClick={() => pick(c.id)}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 + idx * 0.08, duration: 0.4 }}
                whileHover={{ y: -4 }}
                className="group relative bg-white border border-slate-200 rounded-2xl p-6 text-start hover:border-[#006C35] hover:shadow-md transition-all"
              >
                <div className="h-12 w-12 rounded-xl bg-[#006C35]/10 group-hover:bg-[#006C35] group-hover:text-white text-[#006C35] flex items-center justify-center transition-colors mb-5">
                  <Icon className="h-6 w-6" />
                </div>
                <div className="text-xs font-medium text-[#C5A059] tracking-wide uppercase mb-2">
                  {language === "ar" ? c.ar_authority : c.authority}
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2">{l.title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed mb-6">{l.desc}</p>
                <div className="inline-flex items-center gap-2 text-sm font-medium text-[#006C35] group-hover:gap-3 transition-all">
                  {t(language, "continue")}
                  <ArrowRight className={`h-4 w-4 ${language === "ar" ? "rotate-180" : ""}`} />
                </div>
              </motion.button>
            );
          })}
        </div>
      </main>
    </div>
  );
};

export default CategoryPage;
